import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Settings, Star, Pin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  // mainNavigation,
  // accountNavigation,
  // workNavigation,
  // reportsNavigation,
  categories,
} from "./navigation";
import { NavItem } from "./NavItem";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useUserStore } from "@/store/userStore";

export const DesktopSidebar = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [pinnedItems, setPinnedItems] = useState<string[]>([
    "Dashboard",
    "Projects",
    "Messages",
  ]);
  const location = useLocation();
  const { user } = useUserStore();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Load collapsed state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState) {
      setIsCollapsed(savedState === "true");
    }
  }, []);

  // Save collapsed state to localStorage
  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", String(newState));
  };

  // Get pinned items from all categories
  const getPinnedNavItems = () => {
    return categories.all.filter((item) => pinnedItems.includes(item.name));
  };

  return (
    <motion.div
      className="fixed inset-y-0 left-0 z-30 flex flex-col"
      initial={{ x: 0 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className={cn(
          "relative mt-20 h-[calc(100vh-5rem)] transition-all duration-300 ease-in-out border-r",
          isCollapsed ? "w-16" : "w-64",
          isScrolled ? "mt-14 h-full" : "mt-20",
          "bg-white dark:bg-gray-900 shadow-lg backdrop-blur-sm"
        )}
        layout
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={toggleCollapsed}
                className="absolute -right-3 top-4 bg-white dark:bg-gray-800 border shadow-md rounded-full p-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 z-10"
                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4 text-primary" />
                ) : (
                  <ChevronLeft className="w-4 h-4 text-primary" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="bg-gray-900 text-white text-xs"
            >
              {isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div
          className={cn(
            "h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent",
            "flex flex-col pt-6",
            isCollapsed ? "overflow-x-hidden px-2" : "p-3 px-4"
          )}
        >
          {/* <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="mb-4"
              >
                <Tabs
                  value={activeCategory}
                  onValueChange={setActiveCategory}
                  className="w-full"
                >
                  <TabsList className="w-full bg-gray-100/80 dark:bg-gray-800/50 p-0.5 grid-cols-5">
                    <TabsTrigger value="all" className="flex-1 text-xs py-1.5">
                      All
                    </TabsTrigger>
                    <TabsTrigger value="main" className="flex-1 text-xs py-1.5">
                      Main
                    </TabsTrigger>
                    <TabsTrigger value="work" className="flex-1 text-xs py-1.5">
                      Work
                    </TabsTrigger>
                    <TabsTrigger
                      value="reports"
                      className="flex-1 text-xs py-1.5"
                    >
                      Reports
                    </TabsTrigger>
                    <TabsTrigger
                      value="pinned"
                      className="flex-1 text-xs py-1.5"
                    >
                      Pinned
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </motion.div>
            )}
          </AnimatePresence> */}

          {!isCollapsed && activeCategory !== "all" && (
            <div className="flex items-center justify-between mb-2 px-1">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300 capitalize">
                {activeCategory}
              </h3>
              {activeCategory === "pinned" && (
                <button className="text-xs text-gray-500 hover:text-primary transition-colors">
                  Manage
                </button>
              )}
            </div>
          )}

          <div className="space-y-1 py-2">
            {activeCategory === "pinned"
              ? getPinnedNavItems()
                  .filter(
                    (item) =>
                      !item.roles ||
                      item.roles.includes(
                        user?.role as "freelancer" | "Enterprise"
                      )
                  )
                  .map((item) => (
                    <NavItem
                      key={item.name}
                      name={item.name}
                      href={item.href}
                      icon={item.icon}
                      isCollapsed={isCollapsed}
                      badge={item.badge?.count}
                      subItems={item.subItems}
                      isPremium={item.isPremium}
                    />
                  ))
              : categories[activeCategory as keyof typeof categories]
                  ?.filter(
                    (item) =>
                      !item.roles ||
                      item.roles.includes(
                        user?.role as "freelancer" | "Enterprise"
                      )
                  )
                  ?.map((item) => (
                    <NavItem
                      key={item.name}
                      name={item.name}
                      href={item.href}
                      icon={item.icon}
                      isCollapsed={isCollapsed}
                      badge={item.badge?.count}
                      subItems={item.subItems}
                      isPremium={item.isPremium}
                    />
                  ))}
          </div>

          {/* {!isCollapsed && (
            <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="rounded-lg bg-gradient-to-r from-emerald-50 to-sky-50 dark:from-emerald-900/20 dark:to-sky-900/20 p-3 hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 flex items-center justify-center">
                    <Settings className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium">Sidebar Settings</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Customize your sidebar
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </motion.div>
    </motion.div>
  );
};
